<button
	type="button"
	class="posting-override-trigger"
	[class.posting-override-trigger--compact]="compact"
	[class.posting-override-trigger--active]="!!effectiveOverride"
	[disabled]="disabled || loading"
	[title]="tooltipLabel"
	(click)="open()">
	<i class="material-icons posting-override-trigger__icon">account_tree</i>
	<span class="posting-override-trigger__label" *ngIf="!compact">
		<ng-container *ngIf="effectiveOverride; else noOverride">
			Posting&nbsp;<strong>{{ badgeLabel }}</strong>
		</ng-container>
		<ng-template #noOverride>Set&nbsp;posting</ng-template>
	</span>
	<span class="posting-override-trigger__badge" *ngIf="compact && effectiveOverride">{{ badgeLabel }}</span>
	<i class="material-icons posting-override-trigger__chip" *ngIf="!hasOrderNo" title="Will be saved after the order is created.">schedule</i>
</button>

<ng-template #postingOverrideModal let-modal>
	<div class="posting-override">
		<div class="posting-override__header">
			<div>
				<h4 class="posting-override__title" id="posting-override-title">Posting for this order</h4>
				<p class="posting-override__subtitle">
					Optional. Send this order’s inventory, COGS, and sales through a different product posting group when you need it.
					Customer business posting group stays as-is.
				</p>
			</div>
			<button type="button" class="posting-override__close" aria-label="Close" (click)="modal.dismiss('close')">
				<i class="material-icons">close</i>
			</button>
		</div>

		<div class="posting-override__meta" *ngIf="hasOrderNo || effectiveOverride">
			<div class="posting-override__meta-item" *ngIf="hasOrderNo">
				<span class="posting-override__meta-label">Order #</span>
				<span class="posting-override__meta-value">{{ orderno }}</span>
			</div>
			<div class="posting-override__meta-item" *ngIf="!hasOrderNo">
				<span class="posting-override__meta-label">Order #</span>
				<span class="posting-override__meta-value posting-override__meta-value--muted">pending save</span>
			</div>
			<div class="posting-override__meta-item" *ngIf="current?.updated_at">
				<span class="posting-override__meta-label">Last update</span>
				<span class="posting-override__meta-value">{{ current?.updated_at }} <small *ngIf="current?.updated_by">by {{ current?.updated_by }}</small></span>
			</div>
		</div>

		<div class="posting-override__body">
			<div class="posting-override__alert posting-override__alert--info" *ngIf="!hasOrderNo">
				<i class="material-icons">info</i>
				<span>This order has not been saved yet. Your selection will be applied automatically once the order is created.</span>
			</div>

			<div class="posting-override__alert posting-override__alert--error" *ngIf="loadError">
				<i class="material-icons">error</i>
				<span>{{ loadError }}</span>
			</div>

			<div class="posting-override__current" *ngIf="effectiveOverride">
				<div class="posting-override__current-pill">
					<i class="material-icons">verified</i>
					<div>
						<div class="posting-override__current-code">
							{{ effectiveOverride.gen_prod_posting_group }}
							<span class="posting-override__current-flag" *ngIf="!current">staged</span>
						</div>
						<div class="posting-override__current-meta" *ngIf="current?.group?.description">
							{{ current?.group?.description }}
						</div>
						<div class="posting-override__current-meta posting-override__current-meta--accounts" *ngIf="current?.group">
							<span *ngIf="current?.group?.sales_account">Sales: {{ current?.group?.sales_account }}</span>
							<span *ngIf="current?.group?.cogs_account">COGS: {{ current?.group?.cogs_account }}</span>
							<span *ngIf="current?.group?.cogs_account_interim">Interim: {{ current?.group?.cogs_account_interim }}</span>
						</div>
						<div class="posting-override__current-note" *ngIf="effectiveOverride.note">
							<i class="material-icons">sticky_note_2</i> {{ effectiveOverride.note }}
						</div>
					</div>
				</div>
			</div>

			<div class="posting-override__field">
				<label class="posting-override__label" for="posting-override-search">Search</label>
				<div class="posting-override__search">
					<i class="material-icons">search</i>
					<input
						id="posting-override-search"
						type="text"
						class="posting-override__input"
						placeholder="Code, description, or account"
						[(ngModel)]="groupSearch"
						(input)="filterGroups()" />
				</div>
			</div>

			<div class="posting-override__field">
				<label class="posting-override__label">Groups</label>
				<div class="posting-override__group-list">
					<div class="posting-override__loading" *ngIf="groupsLoading">
						<i class="material-icons posting-override__spin">autorenew</i> Loading…
					</div>
					<div class="posting-override__empty" *ngIf="!groupsLoading && filteredGroups.length === 0">
						No matches.
					</div>
					<button
						*ngFor="let g of filteredGroups; trackBy: trackGroup"
						type="button"
						class="posting-override__group"
						[class.posting-override__group--selected]="g.code === draftCode"
						(click)="selectGroup(g)">
						<div class="posting-override__group-head">
							<span class="posting-override__group-code">{{ g.code }}</span>
							<span class="posting-override__group-desc" *ngIf="g.description">{{ g.description }}</span>
						</div>
						<div class="posting-override__group-accounts">
							<span *ngIf="g.sales_account"><strong>Sales</strong> {{ g.sales_account }}</span>
							<span *ngIf="g.cogs_account"><strong>COGS</strong> {{ g.cogs_account }}</span>
							<span *ngIf="g.cogs_account_interim"><strong>Interim</strong> {{ g.cogs_account_interim }}</span>
						</div>
					</button>
				</div>
			</div>

			<div class="posting-override__field">
				<label class="posting-override__label" for="posting-override-note">Note (optional)</label>
				<textarea
					id="posting-override-note"
					class="posting-override__textarea"
					rows="2"
					maxlength="255"
					placeholder="Why is this order being redirected? (visible to A/R)"
					[(ngModel)]="draftNote"></textarea>
			</div>

			<div class="posting-override__alert posting-override__alert--error" *ngIf="saveError">
				<i class="material-icons">error</i>
				<span>{{ saveError }}</span>
			</div>
		</div>

		<div class="posting-override__footer">
			<button type="button" class="posting-override__btn posting-override__btn--ghost" (click)="modal.dismiss('cancel')" [disabled]="saving">
				Cancel
			</button>
			<button
				type="button"
				class="posting-override__btn posting-override__btn--danger"
				[disabled]="saving || (!effectiveOverride && !hasOrderNo)"
				*ngIf="effectiveOverride"
				(click)="clear()">
				<i class="material-icons">delete_sweep</i> Clear override
			</button>
			<button
				type="button"
				class="posting-override__btn posting-override__btn--primary"
				[disabled]="saving || !draftCode"
				(click)="save()">
				<i class="material-icons" *ngIf="!saving">save</i>
				<i class="material-icons posting-override__spin" *ngIf="saving">autorenew</i>
				{{ saving ? 'Saving…' : (effectiveOverride ? 'Update override' : 'Apply override') }}
			</button>
		</div>
	</div>
</ng-template>
